/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import * as XLSX from 'xlsx';
import { 
  Layers, 
  Folder, 
  FolderOpen, 
  Plus, 
  Search, 
  Download, 
  UploadCloud, 
  FileSpreadsheet, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldAlert, 
  Users, 
  Clock, 
  Trash2, 
  Edit3, 
  ArrowLeft, 
  ChevronRight, 
  Award, 
  ShieldCheck, 
  Filter, 
  Building2, 
  UserCheck, 
  FileText,
  Sparkles,
  HelpCircle,
  TrendingUp,
  Flame,
  Zap,
  CheckCircle,
  X,
  Calendar,
  Briefcase,
  ChevronDown
} from 'lucide-react';
import { CostCenter, SafetyActivity, CollaboratorEvaluation, ILULevel } from '../types.ts';
import { 
  INITIAL_SAFETY_ACTIVITIES, 
  INITIAL_COLLABORATOR_EVALUATIONS, 
  ILU_LEVEL_DEFINITIONS 
} from '../data/competenciesInitialData.ts';
import { 
  exportCompetenciesMatrixToExcel, 
  exportSingleActivityDossierToExcel, 
  generateActivitiesTemplateExcel, 
  generateEvaluationsTemplateExcel 
} from '../utils/competenciesExcelExporter.ts';

interface CompetenciesManagerProps {
  activeCostCenter: CostCenter | null;
  costCenters?: CostCenter[];
}

export const CompetenciesManager: React.FC<CompetenciesManagerProps> = ({
  activeCostCenter,
  costCenters = []
}) => {
  const storagePrefix = activeCostCenter ? `snf_security_${activeCostCenter.id}` : 'snf_security_default';

  // State: Activities
  const [activities, setActivities] = useState<SafetyActivity[]>(() => {
    try {
      const saved = localStorage.getItem(`${storagePrefix}_activities_ilu`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Remove old mock records if any
          const clean = parsed.filter(a => !a.id.startsWith('act-00') && a.id !== 'act-001');
          if (clean.length !== parsed.length) {
            localStorage.setItem(`${storagePrefix}_activities_ilu`, JSON.stringify(clean));
          }
          return clean;
        }
      }
    } catch (e) {
      console.warn('Error reading activities from localStorage:', e);
    }
    return INITIAL_SAFETY_ACTIVITIES;
  });

  // State: Collaborator Evaluations
  const [evaluations, setEvaluations] = useState<CollaboratorEvaluation[]>(() => {
    try {
      const saved = localStorage.getItem(`${storagePrefix}_evaluations_ilu`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Remove old mock records if any
          const clean = parsed.filter(e => !e.id.startsWith('eval-00') && e.id !== 'eval-001');
          if (clean.length !== parsed.length) {
            localStorage.setItem(`${storagePrefix}_evaluations_ilu`, JSON.stringify(clean));
          }
          return clean;
        }
      }
    } catch (e) {
      console.warn('Error reading evaluations from localStorage:', e);
    }
    return INITIAL_COLLABORATOR_EVALUATIONS;
  });

  // Reload when switching active cost center
  useEffect(() => {
    try {
      const savedActs = localStorage.getItem(`${storagePrefix}_activities_ilu`);
      if (savedActs) {
        const parsed = JSON.parse(savedActs);
        if (Array.isArray(parsed)) {
          const clean = parsed.filter(a => !a.id.startsWith('act-00') && a.id !== 'act-001');
          setActivities(clean);
        } else {
          setActivities(INITIAL_SAFETY_ACTIVITIES);
        }
      } else {
        setActivities(INITIAL_SAFETY_ACTIVITIES);
      }

      const savedEvals = localStorage.getItem(`${storagePrefix}_evaluations_ilu`);
      if (savedEvals) {
        const parsed = JSON.parse(savedEvals);
        if (Array.isArray(parsed)) {
          const clean = parsed.filter(e => !e.id.startsWith('eval-00') && e.id !== 'eval-001');
          setEvaluations(clean);
        } else {
          setEvaluations(INITIAL_COLLABORATOR_EVALUATIONS);
        }
      } else {
        setEvaluations(INITIAL_COLLABORATOR_EVALUATIONS);
      }
    } catch (e) {
      console.warn('Error switching cost center data:', e);
    }
  }, [storagePrefix]);

  // Helper to persist activities
  const persistActivities = (updated: SafetyActivity[]) => {
    setActivities(updated);
    try {
      localStorage.setItem(`${storagePrefix}_activities_ilu`, JSON.stringify(updated));
    } catch (e) {
      console.error('Error saving activities:', e);
    }
  };

  // Helper to persist evaluations
  const persistEvaluations = (updated: CollaboratorEvaluation[]) => {
    setEvaluations(updated);
    try {
      localStorage.setItem(`${storagePrefix}_evaluations_ilu`, JSON.stringify(updated));
    } catch (e) {
      console.error('Error saving evaluations:', e);
    }
  };

  // Helper to clear all records (delete all test / existing data)
  const handleClearAllData = () => {
    if (window.confirm('¿Está seguro de eliminar TODOS los registros (actividades y evaluaciones) de la matriz de competencias? Esta acción dejará la matriz totalmente limpia y vacía.')) {
      persistActivities([]);
      persistEvaluations([]);
      setSelectedActivityFolder(null);
      localStorage.removeItem(`${storagePrefix}_activities_ilu`);
      localStorage.removeItem(`${storagePrefix}_evaluations_ilu`);
    }
  };

  // Navigation & View Mode
  const [viewMode, setViewMode] = useState<'folders' | 'matrix' | 'evaluations_list' | 'activities_list'>('folders');
  const [selectedActivityFolder, setSelectedActivityFolder] = useState<SafetyActivity | null>(null);

  // Filters & Search
  const [searchFilter, setSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [levelFilter, setLevelFilter] = useState<'all' | 'U' | 'L' | 'I'>('all');
  const [shiftFilter, setShiftFilter] = useState<string>('all');

  // Modals state
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [isBulkActivitiesModalOpen, setIsBulkActivitiesModalOpen] = useState(false);
  const [isBulkEvaluationsModalOpen, setIsBulkEvaluationsModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  // Single Activity Form State
  const [activityForm, setActivityForm] = useState<Partial<SafetyActivity>>({
    riskLevel: 'alto',
    category: 'Trabajos Peligrosos',
    area: activeCostCenter ? activeCostCenter.name : 'Planta General'
  });
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);

  // Single Evaluation Form State
  const [evaluationForm, setEvaluationForm] = useState<Partial<CollaboratorEvaluation>>({
    level: 'U',
    score: undefined,
    status: 'apto',
    evaluationDate: new Date().toISOString().split('T')[0],
    evaluator: '',
    shift: 'Turno 1 - Matutino',
    area: '',
    employeeId: '',
    employeeName: '',
    position: '',
    evidenceNotes: ''
  });
  const [editingEvaluationId, setEditingEvaluationId] = useState<string | null>(null);
  const [activitySearchQuery, setActivitySearchQuery] = useState('');
  const [isActivityDropdownOpen, setIsActivityDropdownOpen] = useState(false);

  // Filtered activities for smart search in modal
  const filteredModalActivities = useMemo(() => {
    if (!activitySearchQuery.trim()) return activities;
    const q = activitySearchQuery.toLowerCase().trim();
    return activities.filter(a => 
      a.code.toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q) ||
      (a.category && a.category.toLowerCase().includes(q)) ||
      (a.standardCode && a.standardCode.toLowerCase().includes(q)) ||
      (a.area && a.area.toLowerCase().includes(q)) ||
      (a.riskLevel && a.riskLevel.toLowerCase().includes(q))
    );
  }, [activities, activitySearchQuery]);

  // Bulk Import States
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkText, setBulkText] = useState('');
  const [bulkParsedActivities, setBulkParsedActivities] = useState<SafetyActivity[]>([]);
  const [bulkParsedEvaluations, setBulkParsedEvaluations] = useState<CollaboratorEvaluation[]>([]);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Overall Statistics Calculation
  const stats = useMemo(() => {
    const totalActs = activities.length;
    const totalEvals = evaluations.length;

    // Unique employees
    const empSet = new Set(evaluations.map(e => e.employeeId || e.employeeName));
    const totalEmployees = empSet.size;

    const countU = evaluations.filter(e => e.level === 'U' || e.level === 'O').length;
    const countL = evaluations.filter(e => e.level === 'L').length;
    const countI = evaluations.filter(e => e.level === 'I').length;

    const pctU = totalEvals > 0 ? Math.round((countU / totalEvals) * 100) : 0;
    const pctL = totalEvals > 0 ? Math.round((countL / totalEvals) * 100) : 0;
    const pctI = totalEvals > 0 ? Math.round((countI / totalEvals) * 100) : 0;

    return {
      totalActs,
      totalEvals,
      totalEmployees,
      countU,
      countL,
      countI,
      pctU,
      pctL,
      pctI
    };
  }, [activities, evaluations]);

  // Categories list for filtering
  const categoriesList = useMemo(() => {
    const cats = new Set(activities.map(a => a.category).filter(Boolean));
    return Array.from(cats);
  }, [activities]);

  // Filtered Activities
  const filteredActivities = useMemo(() => {
    return activities.filter(act => {
      const matchSearch = searchFilter.trim() === '' || 
        act.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        act.code.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (act.area && act.area.toLowerCase().includes(searchFilter.toLowerCase())) ||
        (act.category && act.category.toLowerCase().includes(searchFilter.toLowerCase()));

      const matchCategory = categoryFilter === 'all' || act.category === categoryFilter;
      const matchRisk = riskFilter === 'all' || act.riskLevel === riskFilter;

      return matchSearch && matchCategory && matchRisk;
    });
  }, [activities, searchFilter, categoryFilter, riskFilter]);

  // Evaluations for the currently selected folder activity
  const folderEvaluations = useMemo(() => {
    if (!selectedActivityFolder) return [];
    return evaluations.filter(ev => {
      const matchAct = ev.activityId === selectedActivityFolder.id || ev.activityCode === selectedActivityFolder.code;
      if (!matchAct) return false;

      const matchSearch = searchFilter.trim() === '' ||
        ev.employeeName.toLowerCase().includes(searchFilter.toLowerCase()) ||
        ev.employeeId.toLowerCase().includes(searchFilter.toLowerCase()) ||
        ev.position.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (ev.area && ev.area.toLowerCase().includes(searchFilter.toLowerCase()));

      const matchLevel = levelFilter === 'all' || 
        (levelFilter === 'U' ? (ev.level === 'U' || ev.level === 'O') : ev.level === levelFilter);

      const matchShift = shiftFilter === 'all' || ev.shift === shiftFilter;

      return matchSearch && matchLevel && matchShift;
    });
  }, [selectedActivityFolder, evaluations, searchFilter, levelFilter, shiftFilter]);

  // Folder level counts for selected folder
  const selectedFolderStats = useMemo(() => {
    if (!selectedActivityFolder) return { total: 0, u: 0, l: 0, i: 0, pctU: 0 };
    const allForAct = evaluations.filter(e => e.activityId === selectedActivityFolder.id || e.activityCode === selectedActivityFolder.code);
    const u = allForAct.filter(e => e.level === 'U' || e.level === 'O').length;
    const l = allForAct.filter(e => e.level === 'L').length;
    const i = allForAct.filter(e => e.level === 'I').length;
    const total = allForAct.length;
    const pctU = total > 0 ? Math.round((u / total) * 100) : 0;
    return { total, u, l, i, pctU };
  }, [selectedActivityFolder, evaluations]);

  // Handlers: Excel Export
  const handleExportFullMatrixExcel = async () => {
    try {
      setIsExportingExcel(true);
      await exportCompetenciesMatrixToExcel(activities, evaluations, {
        costCenterName: activeCostCenter ? activeCostCenter.name : 'Planta Industrial General',
        generatedBy: 'Coordinación de Seguridad HSEQ'
      });
    } catch (err) {
      console.error('Error exporting matrix to Excel:', err);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportFolderExcel = async (activity: SafetyActivity) => {
    try {
      setIsExportingExcel(true);
      await exportSingleActivityDossierToExcel(activity, evaluations, {
        costCenterName: activeCostCenter ? activeCostCenter.name : 'Planta Industrial General',
        generatedBy: 'Coordinación de Seguridad HSEQ'
      });
    } catch (err) {
      console.error('Error exporting folder dossier Excel:', err);
    } finally {
      setIsExportingExcel(false);
    }
  };

  // Handlers: Save Activity
  const handleSaveActivity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activityForm.code || !activityForm.name) return;

    const trimmedCode = activityForm.code.trim().toUpperCase();
    const trimmedName = activityForm.name.trim();

    if (editingActivityId) {
      const oldAct = activities.find(a => a.id === editingActivityId);
      const updated = activities.map(a => a.id === editingActivityId ? {
        ...a,
        ...activityForm,
        code: trimmedCode,
        name: trimmedName,
        updatedAt: new Date().toISOString()
      } as SafetyActivity : a);
      persistActivities(updated);

      // Keep evaluations in sync if activity code/name changed
      if (oldAct && (oldAct.code !== trimmedCode || oldAct.name !== trimmedName)) {
        const updatedEvals = evaluations.map(ev => {
          if (ev.activityId === editingActivityId || ev.activityCode === oldAct.code) {
            return {
              ...ev,
              activityCode: trimmedCode,
              activityName: trimmedName
            };
          }
          return ev;
        });
        persistEvaluations(updatedEvals);
      }

      // Update selected folder view if currently open
      if (selectedActivityFolder?.id === editingActivityId) {
        setSelectedActivityFolder(updated.find(a => a.id === editingActivityId) || null);
      }
    } else {
      const newAct: SafetyActivity = {
        id: `act-${Date.now()}`,
        code: trimmedCode,
        name: trimmedName,
        category: activityForm.category || 'Trabajos Peligrosos',
        area: activityForm.area || (activeCostCenter ? activeCostCenter.name : 'Planta General'),
        riskLevel: (activityForm.riskLevel as any) || 'alto',
        description: activityForm.description || '',
        eppRequired: activityForm.eppRequired || '',
        standardCode: activityForm.standardCode || 'NOM-STPS',
        costCenterId: activeCostCenter?.id,
        costCenterName: activeCostCenter?.name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      persistActivities([newAct, ...activities]);
    }

    setIsActivityModalOpen(false);
    setEditingActivityId(null);
    setActivityForm({
      riskLevel: 'alto',
      category: 'Trabajos Peligrosos',
      area: activeCostCenter ? activeCostCenter.name : 'Planta General'
    });
  };

  // Handlers: Delete Activity
  const handleDeleteActivity = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const act = activities.find(a => a.id === id);
    const actLabel = act ? `la actividad "${act.code} - ${act.name}"` : 'esta actividad';
    const associatedCount = evaluations.filter(ev => ev.activityId === id || (act && ev.activityCode === act.code)).length;

    const confirmMsg = associatedCount > 0 
      ? `¿Está seguro de eliminar ${actLabel}? Se eliminarán también las ${associatedCount} evaluaciones de colaboradores asociadas.`
      : `¿Está seguro de eliminar ${actLabel}?`;

    if (window.confirm(confirmMsg)) {
      const updatedActs = activities.filter(a => a.id !== id);
      const updatedEvals = evaluations.filter(ev => ev.activityId !== id && (!act || ev.activityCode !== act.code));
      persistActivities(updatedActs);
      persistEvaluations(updatedEvals);
      if (selectedActivityFolder?.id === id) {
        setSelectedActivityFolder(null);
      }
    }
  };

  // Handlers: Save Evaluation
  const handleSaveEvaluation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!evaluationForm.employeeName || !evaluationForm.activityId) return;

    const targetActivity = activities.find(a => a.id === evaluationForm.activityId);
    const actCode = targetActivity ? targetActivity.code : (evaluationForm.activityCode || 'ACT');
    const actName = targetActivity ? targetActivity.name : (evaluationForm.activityName || 'Actividad');

    if (editingEvaluationId) {
      const updated = evaluations.map(ev => ev.id === editingEvaluationId ? {
        ...ev,
        ...evaluationForm,
        activityCode: actCode,
        activityName: actName,
        updatedAt: new Date().toISOString()
      } as CollaboratorEvaluation : ev);
      persistEvaluations(updated);
    } else {
      const newEval: CollaboratorEvaluation = {
        id: `eval-${Date.now()}`,
        activityId: evaluationForm.activityId,
        activityCode: actCode,
        activityName: actName,
        employeeId: evaluationForm.employeeId?.trim() || `EMP-${Math.floor(1000 + Math.random() * 9000)}`,
        employeeName: evaluationForm.employeeName.trim(),
        position: evaluationForm.position?.trim() || 'Operador Especializado',
        area: evaluationForm.area?.trim() || '',
        shift: evaluationForm.shift || 'Turno 1 - Matutino',
        level: evaluationForm.level || 'U',
        score: evaluationForm.score !== undefined && evaluationForm.score !== null && !isNaN(Number(evaluationForm.score)) ? Number(evaluationForm.score) : 100,
        evaluationDate: evaluationForm.evaluationDate || new Date().toISOString().split('T')[0],
        evaluator: evaluationForm.evaluator?.trim() || '',
        status: (evaluationForm.status as any) || (evaluationForm.level === 'U' ? 'apto' : evaluationForm.level === 'L' ? 'en_desarrollo' : 'requiere_refuerzo'),
        evidenceNotes: evaluationForm.evidenceNotes?.trim() || '',
        costCenterId: activeCostCenter?.id,
        costCenterName: activeCostCenter?.name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      persistEvaluations([newEval, ...evaluations]);
    }

    setIsEvaluationModalOpen(false);
    setEditingEvaluationId(null);
    setActivitySearchQuery('');
    setIsActivityDropdownOpen(false);
    setEvaluationForm({
      level: 'U',
      score: undefined,
      status: 'apto',
      evaluationDate: new Date().toISOString().split('T')[0],
      evaluator: '',
      shift: 'Turno 1 - Matutino',
      area: '',
      employeeId: '',
      employeeName: '',
      position: '',
      evidenceNotes: ''
    });
  };

  // Handlers: Delete Evaluation
  const handleDeleteEvaluation = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const ev = evaluations.find(item => item.id === id);
    const label = ev ? `la evaluación de "${ev.employeeName}" en "${ev.activityCode}"` : 'esta evaluación';
    if (window.confirm(`¿Está seguro de eliminar ${label}?`)) {
      const updated = evaluations.filter(ev => ev.id !== id);
      persistEvaluations(updated);
    }
  };

  // Bulk Activities Parser
  const parseBulkActivitiesFile = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      if (rows.length < 2) {
        setBulkErrors(['El archivo no contiene suficientes filas de datos.']);
        return;
      }

      // Skip headers (assume row 0 or 1 is header)
      let dataRows = rows;
      // If row 0 contains title banner or column names, locate start
      const startIdx = rows.findIndex(r => r && r[0] && (
        String(r[0]).toLowerCase().includes('código') || 
        String(r[0]).toLowerCase().includes('codigo') ||
        String(r[0]).toLowerCase().includes('loto')
      ));

      const actualStart = startIdx >= 0 ? startIdx + 1 : 1;
      const parsed: SafetyActivity[] = [];
      const errors: string[] = [];

      for (let i = actualStart; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.length === 0 || !row[0]) continue;

        const code = String(row[0] || '').trim().toUpperCase();
        const name = String(row[1] || '').trim();
        const category = String(row[2] || 'Trabajos Peligrosos').trim();
        const rawRisk = String(row[3] || 'alto').toLowerCase().trim();
        const standardCode = String(row[4] || 'NOM-STPS').trim();
        const eppRequired = String(row[5] || '').trim();
        const description = String(row[6] || '').trim();

        if (!code || !name) {
          errors.push(`Fila ${i + 1}: Código y Nombre de la actividad son obligatorios.`);
          continue;
        }

        let riskLevel: 'bajo' | 'medio' | 'alto' | 'critico' = 'alto';
        if (rawRisk.includes('crit') || rawRisk.includes('crít')) riskLevel = 'critico';
        else if (rawRisk.includes('alt')) riskLevel = 'alto';
        else if (rawRisk.includes('med')) riskLevel = 'medio';
        else if (rawRisk.includes('baj')) riskLevel = 'bajo';

        parsed.push({
          id: `act-bulk-${Date.now()}-${i}`,
          code,
          name,
          category,
          area: activeCostCenter ? activeCostCenter.name : 'Planta General',
          riskLevel,
          standardCode,
          eppRequired,
          description,
          costCenterId: activeCostCenter?.id,
          costCenterName: activeCostCenter?.name,
          createdAt: new Date().toISOString()
        });
      }

      setBulkParsedActivities(parsed);
      setBulkErrors(errors);
    } catch (err) {
      console.error('Error parsing activities excel:', err);
      setBulkErrors(['Error al leer el archivo Excel. Verifique el formato e inténtelo nuevamente.']);
    }
  };

  // Bulk Evaluations Parser
  const parseBulkEvaluationsFile = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      const startIdx = rows.findIndex(r => r && r[0] && (
        String(r[0]).toLowerCase().includes('código') || 
        String(r[0]).toLowerCase().includes('codigo') ||
        String(r[0]).toLowerCase().includes('loto')
      ));

      const actualStart = startIdx >= 0 ? startIdx + 1 : 1;
      const parsed: CollaboratorEvaluation[] = [];
      const errors: string[] = [];

      for (let i = actualStart; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.length === 0 || !row[0]) continue;

        const actCode = String(row[0] || '').trim().toUpperCase();
        const empId = String(row[1] || '').trim();
        const empName = String(row[2] || '').trim();
        const position = String(row[3] || 'Operador').trim();
        const area = String(row[4] || 'Producción').trim();
        const shift = String(row[5] || 'Turno 1 - Matutino').trim();
        const rawLevel = String(row[6] || 'U').trim().toUpperCase();
        const score = Number(row[7]) || 100;
        const evalDate = String(row[8] || new Date().toISOString().split('T')[0]).trim();
        const evaluator = String(row[9] || 'Líder HSEQ').trim();
        const notes = String(row[10] || '').trim();

        if (!actCode || !empName) {
          errors.push(`Fila ${i + 1}: Código de Actividad y Nombre del Colaborador son obligatorios.`);
          continue;
        }

        let level: ILULevel = 'U';
        if (['I', 'L', 'U', 'O'].includes(rawLevel)) {
          level = rawLevel as ILULevel;
        }

        // Match existing activity if any
        const matchedAct = activities.find(a => a.code === actCode);
        const actId = matchedAct ? matchedAct.id : `act-auto-${actCode}`;
        const actName = matchedAct ? matchedAct.name : `Actividad ${actCode}`;

        let status: 'apto' | 'en_desarrollo' | 'requiere_refuerzo' = 'apto';
        if (level === 'U' || level === 'O') status = 'apto';
        else if (level === 'L') status = 'en_desarrollo';
        else status = 'requiere_refuerzo';

        parsed.push({
          id: `eval-bulk-${Date.now()}-${i}`,
          activityId: actId,
          activityCode: actCode,
          activityName: actName,
          employeeId: empId || `EMP-${Math.floor(1000 + Math.random() * 9000)}`,
          employeeName: empName,
          position,
          area,
          shift,
          level,
          score,
          evaluationDate: evalDate,
          evaluator,
          status,
          evidenceNotes: notes,
          costCenterId: activeCostCenter?.id,
          costCenterName: activeCostCenter?.name,
          createdAt: new Date().toISOString()
        });
      }

      setBulkParsedEvaluations(parsed);
      setBulkErrors(errors);
    } catch (err) {
      console.error('Error parsing evaluations excel:', err);
      setBulkErrors(['Error al leer el archivo Excel de evaluaciones.']);
    }
  };

  // Confirm Bulk Activities Import
  const handleConfirmBulkActivities = () => {
    if (bulkParsedActivities.length === 0) return;
    // Merge or prepend
    const existingCodes = new Set(activities.map(a => a.code));
    const newItems = bulkParsedActivities.filter(a => !existingCodes.has(a.code));
    const merged = [...newItems, ...activities];
    persistActivities(merged);
    setIsBulkActivitiesModalOpen(false);
    setBulkParsedActivities([]);
    setBulkFile(null);
  };

  // Confirm Bulk Evaluations Import
  const handleConfirmBulkEvaluations = () => {
    if (bulkParsedEvaluations.length === 0) return;
    const merged = [...bulkParsedEvaluations, ...evaluations];
    persistEvaluations(merged);
    setIsBulkEvaluationsModalOpen(false);
    setBulkParsedEvaluations([]);
    setBulkFile(null);
  };

  return (
    <div className="bg-[#121317] border border-[#262836] rounded-2xl p-4 sm:p-6 space-y-6 text-white animate-fadeIn">
      
      {/* 1. TOP HEADER & MAIN CONTROLS */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-[#222430] pb-5">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-indigo-900 border border-indigo-500/40 flex items-center justify-center text-white shadow-lg">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                Matriz de Competencias y Destrezas HSEQ (Método I.L.U.)
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-indigo-950 text-indigo-300 border border-indigo-500/40">
                  {activeCostCenter ? activeCostCenter.name : 'Planta Industrial'}
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Gestión de actividades críticas, carpetas por tarea, evaluación por niveles (I-L-U) y reporte oficial en Excel.
              </p>
            </div>
          </div>
        </div>

        {/* Global Action Buttons */}
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => setIsHelpModalOpen(true)}
            className="p-2 rounded-xl bg-[#1a1b23] hover:bg-[#252836] text-slate-300 border border-[#2d3040] transition-colors cursor-pointer text-xs flex items-center gap-1.5"
            title="Ver Criterios de Niveles I.L.U."
          >
            <HelpCircle className="w-4 h-4 text-amber-400" />
            <span className="hidden sm:inline">Criterios I.L.U.</span>
          </button>

          {/* Clear / Reset All Data button (when records exist) */}
          {(activities.length > 0 || evaluations.length > 0) && (
            <button
              onClick={handleClearAllData}
              className="p-2 rounded-xl bg-red-950/40 hover:bg-red-900/60 text-red-300 hover:text-white border border-red-500/40 transition-colors cursor-pointer text-xs flex items-center gap-1.5"
              title="Borrar todos los registros y evaluaciones de prueba para comenzar en limpio"
            >
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
              <span className="hidden sm:inline">Vaciar Todo</span>
            </button>
          )}

          <button
            onClick={handleExportFullMatrixExcel}
            disabled={isExportingExcel || activities.length === 0}
            className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-2 transition-all shadow-md shadow-emerald-950/40 cursor-pointer disabled:opacity-50"
            title="Descargar Matriz Completa en Excel con Hojas Desglosadas por Nivel U, L, I"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>{isExportingExcel ? 'Generando...' : 'Descargar Excel Matriz'}</span>
          </button>

          <button
            onClick={() => {
              setActivityForm({
                riskLevel: 'alto',
                category: 'Trabajos Peligrosos',
                area: activeCostCenter ? activeCostCenter.name : 'Planta General'
              });
              setEditingActivityId(null);
              setIsActivityModalOpen(true);
            }}
            className="px-3 py-2 rounded-xl bg-[#1e2029] hover:bg-[#2a2d3b] text-slate-200 border border-[#34374a] text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <Plus className="w-4 h-4 text-sky-400" />
            <span>Nueva Actividad</span>
          </button>

          <button
            onClick={() => {
              setBulkParsedActivities([]);
              setBulkErrors([]);
              setBulkFile(null);
              setIsBulkActivitiesModalOpen(true);
            }}
            className="px-3 py-2 rounded-xl bg-[#1e2029] hover:bg-[#2a2d3b] text-slate-200 border border-[#34374a] text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <UploadCloud className="w-4 h-4 text-emerald-400" />
            <span>Carga Masiva Actividades</span>
          </button>

          <button
            onClick={() => {
              setActivitySearchQuery('');
              setIsActivityDropdownOpen(false);
              setEvaluationForm({
                activityId: activities[0]?.id || '',
                level: 'U',
                score: undefined,
                status: 'apto',
                evaluationDate: new Date().toISOString().split('T')[0],
                evaluator: '',
                shift: 'Turno 1 - Matutino',
                area: '',
                employeeId: '',
                employeeName: '',
                position: '',
                evidenceNotes: ''
              });
              setEditingEvaluationId(null);
              setIsEvaluationModalOpen(true);
            }}
            className="px-4 py-2 rounded-xl bg-[#bc1100] hover:bg-[#d91605] text-white text-xs font-bold flex items-center gap-1.5 shadow-md shadow-red-950/40 cursor-pointer"
          >
            <UserCheck className="w-4 h-4" />
            <span>Evaluar Colaborador</span>
          </button>
        </div>
      </div>

      {/* 2. KPI SUMMARY BANNER */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        
        {/* Total Activities */}
        <div className="bg-[#171821] border border-[#292b38] rounded-xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">Actividades</span>
            <Layers className="w-3.5 h-3.5 text-indigo-400" />
          </div>
          <div className="text-xl font-black text-white">{stats.totalActs}</div>
          <p className="text-[10px] text-slate-400">Tareas Críticas</p>
        </div>

        {/* Total Collaborators */}
        <div className="bg-[#171821] border border-[#292b38] rounded-xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">Personal</span>
            <Users className="w-3.5 h-3.5 text-sky-400" />
          </div>
          <div className="text-xl font-black text-white">{stats.totalEmployees}</div>
          <p className="text-[10px] text-slate-400">Colaboradores</p>
        </div>

        {/* Total Evaluations */}
        <div className="bg-[#171821] border border-[#292b38] rounded-xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">Evaluaciones</span>
            <CheckCircle2 className="w-3.5 h-3.5 text-purple-400" />
          </div>
          <div className="text-xl font-black text-white">{stats.totalEvals}</div>
          <p className="text-[10px] text-slate-400">Padrón Registrado</p>
        </div>

        {/* Level U - Autonomous */}
        <div className="bg-emerald-950/20 border border-emerald-500/30 rounded-xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-emerald-300">
            <span className="text-[10px] font-bold uppercase tracking-wider">Nivel U (Autónomos)</span>
            <span className="text-xs">🟢</span>
          </div>
          <div className="text-xl font-black text-emerald-400 flex items-baseline gap-1.5">
            <span>{stats.countU}</span>
            <span className="text-xs font-mono text-emerald-300/80">({stats.pctU}%)</span>
          </div>
          <p className="text-[10px] text-emerald-300/70">100% Capacitados / Aptos</p>
        </div>

        {/* Level L - Supervised */}
        <div className="bg-sky-950/20 border border-sky-500/30 rounded-xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-sky-300">
            <span className="text-[10px] font-bold uppercase tracking-wider">Nivel L (Supervisados)</span>
            <span className="text-xs">🔵</span>
          </div>
          <div className="text-xl font-black text-sky-400 flex items-baseline gap-1.5">
            <span>{stats.countL}</span>
            <span className="text-xs font-mono text-sky-300/80">({stats.pctL}%)</span>
          </div>
          <p className="text-[10px] text-sky-300/70">En Práctica / Con Tutor</p>
        </div>

        {/* Level I - Induction */}
        <div className="bg-amber-950/20 border border-amber-500/30 rounded-xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-amber-300">
            <span className="text-[10px] font-bold uppercase tracking-wider">Nivel I (Inducción)</span>
            <span className="text-xs">🟠</span>
          </div>
          <div className="text-xl font-black text-amber-400 flex items-baseline gap-1.5">
            <span>{stats.countI}</span>
            <span className="text-xs font-mono text-amber-300/80">({stats.pctI}%)</span>
          </div>
          <p className="text-[10px] text-amber-300/70">En Formación Teórica</p>
        </div>

      </div>

      {/* 3. VIEW MODE TABS */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#222430] pb-3">
        <div className="flex items-center gap-1.5 bg-[#171821] p-1 rounded-xl border border-[#272938]">
          <button
            onClick={() => {
              setViewMode('folders');
              setSelectedActivityFolder(null);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'folders'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Folder className="w-3.5 h-3.5" />
            <span>Carpetas por Actividad</span>
          </button>

          <button
            onClick={() => {
              setViewMode('matrix');
              setSelectedActivityFolder(null);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'matrix'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Matriz Cruzada Global</span>
          </button>

          <button
            onClick={() => {
              setViewMode('evaluations_list');
              setSelectedActivityFolder(null);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'evaluations_list'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Padrón de Evaluados ({evaluations.length})</span>
          </button>

          <button
            onClick={() => {
              setViewMode('activities_list');
              setSelectedActivityFolder(null);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'activities_list'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Briefcase className="w-3.5 h-3.5" />
            <span>Catálogo Actividades ({activities.length})</span>
          </button>
        </div>

        {/* Quick templates download buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => generateActivitiesTemplateExcel()}
            className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-[#1a1b24] hover:bg-[#262838] text-slate-300 border border-[#2d3042] flex items-center gap-1 cursor-pointer"
            title="Descargar plantilla Excel para carga masiva de Actividades"
          >
            <Download className="w-3 h-3 text-sky-400" />
            <span>Plantilla Actividades</span>
          </button>

          <button
            onClick={() => generateEvaluationsTemplateExcel(activities)}
            className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-[#1a1b24] hover:bg-[#262838] text-slate-300 border border-[#2d3042] flex items-center gap-1 cursor-pointer"
            title="Descargar plantilla Excel para evaluación masiva de Colaboradores"
          >
            <Download className="w-3 h-3 text-emerald-400" />
            <span>Plantilla Evaluaciones</span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* VISTA 1: CARPETAS POR ACTIVIDAD (EXPEDIENTES INDIVIDUALES) */}
      {/* ========================================================================= */}
      {viewMode === 'folders' && !selectedActivityFolder && (
        <div className="space-y-5">
          
          {/* Filters & Search Bar */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-[#161720] p-3.5 rounded-xl border border-[#282a3a]">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar carpeta por nombre, código de actividad o área..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-[#101115] border border-[#2a2c3c] rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-indigo-500"
              >
                <option value="all">Todas las Categorías</option>
                {categoriesList.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                className="bg-[#101115] border border-[#2a2c3c] rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-indigo-500"
              >
                <option value="all">Todos los Riesgos</option>
                <option value="critico">Riesgo Crítico</option>
                <option value="alto">Riesgo Alto</option>
                <option value="medio">Riesgo Medio</option>
                <option value="bajo">Riesgo Bajo</option>
              </select>

              {(searchFilter || categoryFilter !== 'all' || riskFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchFilter('');
                    setCategoryFilter('all');
                    setRiskFilter('all');
                  }}
                  className="px-2.5 py-2 text-xs text-slate-400 hover:text-white bg-[#101115] border border-[#2a2c3c] rounded-xl cursor-pointer"
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>

          {/* Folders Grid */}
          {filteredActivities.length === 0 ? (
            <div className="text-center py-16 px-4 rounded-2xl bg-[#14151b] border border-[#242634] space-y-3">
              <Folder className="w-12 h-12 text-slate-600 mx-auto" />
              <h4 className="text-sm font-bold text-white">No se encontraron carpetas de actividades</h4>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                No hay actividades que coincidan con los filtros aplicados. Cree una nueva actividad o importe la plantilla masiva.
              </p>
              <button
                onClick={() => setIsActivityModalOpen(true)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold cursor-pointer inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                <span>Crear Primera Actividad</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredActivities.map(activity => {
                const actEvals = evaluations.filter(e => e.activityId === activity.id || e.activityCode === activity.code);
                const uCount = actEvals.filter(e => e.level === 'U' || e.level === 'O').length;
                const lCount = actEvals.filter(e => e.level === 'L').length;
                const iCount = actEvals.filter(e => e.level === 'I').length;
                const totalEvaluated = actEvals.length;
                const pctAutonomy = totalEvaluated > 0 ? Math.round((uCount / totalEvaluated) * 100) : 0;

                const isCrit = activity.riskLevel === 'critico';
                const isHigh = activity.riskLevel === 'alto';

                return (
                  <motion.div
                    key={activity.id}
                    whileHover={{ y: -3 }}
                    transition={{ duration: 0.15 }}
                    onClick={() => {
                      setSelectedActivityFolder(activity);
                      setSearchFilter('');
                      setLevelFilter('all');
                      setShiftFilter('all');
                    }}
                    className="bg-[#161720] hover:bg-[#1a1c26] border border-[#272938] hover:border-indigo-500/50 rounded-2xl p-5 space-y-4 cursor-pointer transition-all shadow-lg relative group overflow-hidden"
                  >
                    {/* Top Folder Tab Shape & Code */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <div className={`p-2.5 rounded-xl border flex items-center justify-center ${
                          isCrit ? 'bg-red-950/40 border-red-500/40 text-red-400' :
                          isHigh ? 'bg-amber-950/40 border-amber-500/40 text-amber-400' :
                          'bg-indigo-950/40 border-indigo-500/40 text-indigo-400'
                        }`}>
                          <Folder className="w-5 h-5 group-hover:hidden" />
                          <FolderOpen className="w-5 h-5 hidden group-hover:block" />
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="px-2 py-0.5 rounded-md bg-slate-900 border border-slate-700 text-white font-mono font-black text-[11px]">
                              {activity.code}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                              isCrit ? 'bg-red-500/20 text-red-300 border border-red-500/40' :
                              isHigh ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' :
                              'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                            }`}>
                              {activity.riskLevel}
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400 truncate block mt-0.5">
                            {activity.category} • {activity.area}
                          </span>
                        </div>
                      </div>

                      {/* Folder Card Action Buttons */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingActivityId(activity.id);
                            setActivityForm({ ...activity });
                            setIsActivityModalOpen(true);
                          }}
                          className="p-1.5 rounded-lg bg-[#20222e] hover:bg-sky-600 text-slate-400 hover:text-white border border-[#2e3142] transition-colors cursor-pointer"
                          title="Editar ficha de esta actividad"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={(e) => handleDeleteActivity(activity.id, e)}
                          className="p-1.5 rounded-lg bg-[#20222e] hover:bg-red-600 text-slate-400 hover:text-white border border-[#2e3142] transition-colors cursor-pointer"
                          title="Eliminar actividad"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportFolderExcel(activity);
                          }}
                          className="p-1.5 rounded-lg bg-[#20222e] hover:bg-emerald-600 text-slate-400 hover:text-white border border-[#2e3142] transition-colors cursor-pointer"
                          title="Descargar reporte Excel de esta actividad"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Activity Name */}
                    <div>
                      <h4 className="font-bold text-sm text-white line-clamp-2 group-hover:text-indigo-300 transition-colors">
                        {activity.name}
                      </h4>
                      {activity.standardCode && (
                        <p className="text-[10px] text-slate-400 font-mono mt-1">
                          📜 {activity.standardCode}
                        </p>
                      )}
                    </div>

                    {/* ILU Levels Distribution Bar */}
                    <div className="space-y-1.5 pt-2 border-t border-[#222432]">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-400 font-medium">Colaboradores Evaluados:</span>
                        <span className="font-bold text-white font-mono">{totalEvaluated}</span>
                      </div>

                      <div className="grid grid-cols-3 gap-1 text-[10px] font-mono text-center">
                        <div className="bg-emerald-950/40 border border-emerald-500/30 rounded-lg py-1 text-emerald-300">
                          <span className="block font-bold">🟢 U: {uCount}</span>
                          <span className="text-[8px] text-slate-400">Autónomo</span>
                        </div>
                        <div className="bg-sky-950/40 border border-sky-500/30 rounded-lg py-1 text-sky-300">
                          <span className="block font-bold">🔵 L: {lCount}</span>
                          <span className="text-[8px] text-slate-400">Supervisado</span>
                        </div>
                        <div className="bg-amber-950/40 border border-amber-500/30 rounded-lg py-1 text-amber-300">
                          <span className="block font-bold">🟠 I: {iCount}</span>
                          <span className="text-[8px] text-slate-400">Inducción</span>
                        </div>
                      </div>

                      {/* Autonomy Progress Bar */}
                      <div className="space-y-1 pt-1">
                        <div className="flex justify-between text-[10px] text-slate-400">
                          <span>Índice de Autonomía:</span>
                          <span className="font-bold text-emerald-400">{pctAutonomy}% Nivel U</span>
                        </div>
                        <div className="w-full bg-[#101115] h-1.5 rounded-full overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-emerald-600 to-emerald-400 h-full rounded-full transition-all"
                            style={{ width: `${pctAutonomy}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Footer / Open Dossier prompt */}
                    <div className="flex items-center justify-between text-[11px] text-indigo-400 group-hover:text-indigo-300 pt-1 font-bold">
                      <span>Abrir Expediente de Actividad</span>
                      <ChevronRight className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" />
                    </div>

                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* VISTA 1.1: DETALLE DE EXPEDIENTE DE CARPETA SELECCIONADA */}
      {/* ========================================================================= */}
      {viewMode === 'folders' && selectedActivityFolder && (
        <div className="space-y-6">
          
          {/* Breadcrumb & Navigation */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#161720] p-4 rounded-2xl border border-[#282a3a]">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedActivityFolder(null)}
                className="p-2 rounded-xl bg-[#1f212c] hover:bg-[#2b2e3e] text-slate-200 border border-[#323548] transition-colors cursor-pointer flex items-center gap-1 text-xs font-bold"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Volver a Carpetas</span>
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-md bg-indigo-950 text-indigo-300 border border-indigo-500/40 text-xs font-mono font-bold">
                    {selectedActivityFolder.code}
                  </span>
                  <h3 className="text-base font-bold text-white">
                    {selectedActivityFolder.name}
                  </h3>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  {selectedActivityFolder.category} • {selectedActivityFolder.area} • Riesgo {(selectedActivityFolder.riskLevel || 'medio').toUpperCase()}
                </p>
              </div>
            </div>

            {/* Folder Actions */}
            <div className="flex flex-wrap items-center gap-2 self-end sm:self-center">
              <button
                onClick={() => {
                  setEditingActivityId(selectedActivityFolder.id);
                  setActivityForm({ ...selectedActivityFolder });
                  setIsActivityModalOpen(true);
                }}
                className="px-3 py-2 rounded-xl bg-[#1f212c] hover:bg-sky-600 text-slate-200 hover:text-white border border-[#323548] text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer transition-colors"
                title="Editar datos y requisitos de esta actividad"
              >
                <Edit3 className="w-3.5 h-3.5 text-sky-400 group-hover:text-white" />
                <span>Editar Ficha</span>
              </button>

              <button
                onClick={(e) => handleDeleteActivity(selectedActivityFolder.id, e)}
                className="px-3 py-2 rounded-xl bg-[#1f212c] hover:bg-red-600 text-slate-200 hover:text-white border border-[#323548] text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer transition-colors"
                title="Eliminar esta actividad y sus evaluaciones"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-400 group-hover:text-white" />
                <span>Eliminar</span>
              </button>

              <button
                onClick={() => handleExportFolderExcel(selectedActivityFolder)}
                disabled={isExportingExcel}
                className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer disabled:opacity-50"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" />
                <span>Descargar Excel</span>
              </button>

              <button
                onClick={() => {
                  setActivitySearchQuery('');
                  setIsActivityDropdownOpen(false);
                  setEvaluationForm({
                    activityId: selectedActivityFolder.id,
                    activityCode: selectedActivityFolder.code,
                    activityName: selectedActivityFolder.name,
                    level: 'U',
                    score: undefined,
                    status: 'apto',
                    evaluationDate: new Date().toISOString().split('T')[0],
                    evaluator: '',
                    shift: 'Turno 1 - Matutino',
                    area: '',
                    employeeId: '',
                    employeeName: '',
                    position: '',
                    evidenceNotes: ''
                  });
                  setEditingEvaluationId(null);
                  setIsEvaluationModalOpen(true);
                }}
                className="px-3.5 py-2 rounded-xl bg-[#bc1100] hover:bg-[#d91605] text-white text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Evaluar Colaborador</span>
              </button>
            </div>
          </div>

          {/* Dossier Technical Requirements Panel */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 bg-[#14151c] p-4 rounded-xl border border-[#252736] text-xs">
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
                <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
                Norma / Estándar STPS:
              </span>
              <p className="font-mono text-slate-200 font-bold">
                {selectedActivityFolder.standardCode || 'NOM-STPS de Referencia'}
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-sky-400" />
                EPP y Controles Requeridos:
              </span>
              <p className="text-slate-300">
                {selectedActivityFolder.eppRequired || 'EPP básico de seguridad según matriz de puesto'}
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5 text-indigo-400" />
                Descripción Operativa:
              </span>
              <p className="text-slate-300 line-clamp-2">
                {selectedActivityFolder.description || 'Procedimiento estándar de operación segura.'}
              </p>
            </div>
          </div>

          {/* Level Filter Tabs for this folder */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#161720] p-3 rounded-xl border border-[#282a3a]">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setLevelFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  levelFilter === 'all'
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-400 hover:text-white bg-[#101115]'
                }`}
              >
                Todos ({selectedFolderStats.total})
              </button>

              <button
                onClick={() => setLevelFilter('U')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  levelFilter === 'U'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'text-emerald-400 hover:bg-emerald-950/40 bg-[#101115] border border-emerald-500/30'
                }`}
              >
                <span>🟢 Nivel U - Autónomos ({selectedFolderStats.u})</span>
              </button>

              <button
                onClick={() => setLevelFilter('L')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  levelFilter === 'L'
                    ? 'bg-sky-600 text-white shadow-md'
                    : 'text-sky-400 hover:bg-sky-950/40 bg-[#101115] border border-sky-500/30'
                }`}
              >
                <span>🔵 Nivel L - Supervisados ({selectedFolderStats.l})</span>
              </button>

              <button
                onClick={() => setLevelFilter('I')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  levelFilter === 'I'
                    ? 'bg-amber-600 text-white shadow-md'
                    : 'text-amber-400 hover:bg-amber-950/40 bg-[#101115] border border-amber-500/30'
                }`}
              >
                <span>🟠 Nivel I - Inducción ({selectedFolderStats.i})</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={shiftFilter}
                onChange={(e) => setShiftFilter(e.target.value)}
                className="bg-[#101115] border border-[#2a2c3c] rounded-xl px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none"
              >
                <option value="all">Todos los Turnos</option>
                <option value="Turno 1 - Matutino">Turno 1 - Matutino</option>
                <option value="Turno 2 - Vespertino">Turno 2 - Vespertino</option>
                <option value="Turno 3 - Nocturno">Turno 3 - Nocturno</option>
                <option value="Turno Mixto">Turno Mixto</option>
                <option value="Administrativo">Administrativo</option>
              </select>

              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar colaborador..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="bg-[#101115] border border-[#2a2c3c] rounded-xl pl-8 pr-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Collaborators Evaluated in this Folder Table */}
          {folderEvaluations.length === 0 ? (
            <div className="text-center py-12 px-4 rounded-xl bg-[#14151c] border border-[#242634] space-y-2">
              <Users className="w-10 h-10 text-slate-600 mx-auto" />
              <h4 className="text-sm font-bold text-white">No hay colaboradores evaluados con estos filtros</h4>
              <p className="text-xs text-slate-400">
                Haga clic en "Evaluar Colaborador" para registrar la primera evaluación en esta actividad.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#282a3a]">
              <table className="w-full text-left text-xs">
                <thead className="bg-[#161722] text-slate-400 uppercase text-[10px] tracking-wider border-b border-[#282a3a]">
                  <tr>
                    <th className="p-3">Colaborador</th>
                    <th className="p-3">Puesto / Rol</th>
                    <th className="p-3">Área</th>
                    <th className="p-3">Turno</th>
                    <th className="p-3 text-center">Nivel I.L.U.</th>
                    <th className="p-3 text-center">Score</th>
                    <th className="p-3">Fecha Eval.</th>
                    <th className="p-3">Auditor</th>
                    <th className="p-3">Estatus</th>
                    <th className="p-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#202230] bg-[#121318]">
                  {folderEvaluations.map(ev => {
                    const isU = ev.level === 'U' || ev.level === 'O';
                    const isL = ev.level === 'L';
                    const isI = ev.level === 'I';

                    return (
                      <tr key={ev.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="p-3">
                          <div className="font-bold text-white">{ev.employeeName}</div>
                          <div className="text-[10px] text-slate-400 font-mono">ID: {ev.employeeId || 'S/N'}</div>
                        </td>
                        <td className="p-3 text-slate-300">{ev.position}</td>
                        <td className="p-3 text-slate-400">{ev.area}</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[10px]">
                            {ev.shift}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-black font-mono border ${
                            isU ? 'bg-emerald-950/70 text-emerald-300 border-emerald-500/50' :
                            isL ? 'bg-sky-950/70 text-sky-300 border-sky-500/50' :
                            'bg-amber-950/70 text-amber-300 border-amber-500/50'
                          }`}>
                            <span>{ILU_LEVEL_DEFINITIONS[ev.level]?.iconSymbol || '🟢'}</span>
                            <span>NIVEL {ev.level}</span>
                          </span>
                        </td>
                        <td className="p-3 text-center font-mono font-bold text-slate-200">
                          {ev.score ? `${ev.score}%` : '100%'}
                        </td>
                        <td className="p-3 text-slate-300 font-mono text-[11px]">
                          {ev.evaluationDate}
                        </td>
                        <td className="p-3 text-slate-400 text-[11px]">
                          {ev.evaluator}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            ev.status === 'apto' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' :
                            ev.status === 'en_desarrollo' ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40' :
                            'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                          }`}>
                            {ev.status === 'apto' ? 'Apto' : (ev.status === 'en_desarrollo' ? 'En Desarrollo' : 'Refuerzo')}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => {
                                setEditingEvaluationId(ev.id);
                                setEvaluationForm({ ...ev });
                                setIsEvaluationModalOpen(true);
                              }}
                              className="p-1.5 text-slate-400 hover:text-sky-400 hover:bg-[#1a1b24] rounded-lg transition-colors cursor-pointer"
                              title="Editar evaluación"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteEvaluation(ev.id, e)}
                              className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-[#1a1b24] rounded-lg transition-colors cursor-pointer"
                              title="Eliminar evaluación"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}

      {/* ========================================================================= */}
      {/* VISTA 2: MATRIZ CRUZADA GLOBAL (COLABORADORES VS ACTIVIDADES) */}
      {/* ========================================================================= */}
      {viewMode === 'matrix' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-[#161720] p-3.5 rounded-xl border border-[#282a3a]">
            <div>
              <h4 className="font-bold text-sm text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-400" />
                Matriz Cruzada de Cobertura y Aptitud por Puesto
              </h4>
              <p className="text-xs text-slate-400">
                Visualice el nivel I.L.U. de cada colaborador a través de todas las actividades críticas registradas.
              </p>
            </div>
            <button
              onClick={handleExportFullMatrixExcel}
              className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-md"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>Exportar Matriz Excel</span>
            </button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-[#282a3a]">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-[#161722] text-slate-300 uppercase text-[10px] tracking-wider border-b border-[#282a3a]">
                <tr>
                  <th className="p-3 sticky left-0 bg-[#161722] z-10 min-w-[200px]">Colaborador / Puesto</th>
                  <th className="p-3 min-w-[100px]">Área</th>
                  <th className="p-3 min-w-[120px]">Turno</th>
                  {activities.map(act => (
                    <th key={act.id} className="p-3 text-center min-w-[110px]" title={act.name}>
                      <div className="font-mono font-bold text-white">{act.code}</div>
                      <div className="text-[9px] text-slate-400 truncate max-w-[100px]">{act.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#202230] bg-[#121318]">
                {Array.from(new Set(evaluations.map(e => e.employeeId || e.employeeName))).map(empKey => {
                  const empEvals = evaluations.filter(e => (e.employeeId || e.employeeName) === empKey);
                  const firstEval = empEvals[0];
                  if (!firstEval) return null;

                  return (
                    <tr key={empKey} className="hover:bg-white/[0.02]">
                      <td className="p-3 sticky left-0 bg-[#121318] z-10 border-r border-[#202230]">
                        <div className="font-bold text-white">{firstEval.employeeName}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{firstEval.position} • {firstEval.employeeId}</div>
                      </td>
                      <td className="p-3 text-slate-400">{firstEval.area}</td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[10px]">
                          {firstEval.shift}
                        </span>
                      </td>
                      {activities.map(act => {
                        const ev = empEvals.find(e => e.activityId === act.id || e.activityCode === act.code);
                        if (!ev) {
                          return (
                            <td key={act.id} className="p-3 text-center text-slate-600 font-mono">
                              -
                            </td>
                          );
                        }

                        const isU = ev.level === 'U' || ev.level === 'O';
                        const isL = ev.level === 'L';

                        return (
                          <td key={act.id} className="p-3 text-center">
                            <span 
                              onClick={() => {
                                setEditingEvaluationId(ev.id);
                                setEvaluationForm({ ...ev });
                                setIsEvaluationModalOpen(true);
                              }}
                              className={`inline-block px-2 py-0.5 rounded font-mono font-black text-[11px] cursor-pointer hover:scale-110 transition-transform ${
                                isU ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/50' :
                                isL ? 'bg-sky-950 text-sky-300 border border-sky-500/50' :
                                'bg-amber-950 text-amber-300 border border-amber-500/50'
                              }`}
                              title={`${ev.employeeName} - ${act.name}\nNivel: ${ev.level} (${ev.score || 100}%)\nFecha: ${ev.evaluationDate}\nClic para editar`}
                            >
                              {ev.level}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VISTA 3: PADRÓN GENERAL DE EVALUACIONES */}
      {/* ========================================================================= */}
      {viewMode === 'evaluations_list' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#161720] p-3.5 rounded-xl border border-[#282a3a]">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por colaborador, actividad, puesto o evaluador..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setBulkParsedEvaluations([]);
                  setBulkErrors([]);
                  setBulkFile(null);
                  setIsBulkEvaluationsModalOpen(true);
                }}
                className="px-3 py-1.5 rounded-xl bg-[#1e2029] hover:bg-[#2a2d3b] text-slate-200 border border-[#34374a] text-xs font-bold flex items-center gap-1.5 cursor-pointer"
              >
                <UploadCloud className="w-3.5 h-3.5 text-emerald-400" />
                <span>Carga Masiva Evaluaciones</span>
              </button>

              <button
                onClick={() => {
                  setActivitySearchQuery('');
                  setIsActivityDropdownOpen(false);
                  setEvaluationForm({
                    activityId: activities[0]?.id || '',
                    level: 'U',
                    score: undefined,
                    status: 'apto',
                    evaluationDate: new Date().toISOString().split('T')[0],
                    evaluator: '',
                    shift: 'Turno 1 - Matutino',
                    area: '',
                    employeeId: '',
                    employeeName: '',
                    position: '',
                    evidenceNotes: ''
                  });
                  setEditingEvaluationId(null);
                  setIsEvaluationModalOpen(true);
                }}
                className="px-3.5 py-1.5 rounded-xl bg-[#bc1100] hover:bg-[#d91605] text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-md"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Nueva Evaluación</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-[#282a3a]">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#161722] text-slate-400 uppercase text-[10px] tracking-wider border-b border-[#282a3a]">
                <tr>
                  <th className="p-3">Código</th>
                  <th className="p-3">Actividad Evaluada</th>
                  <th className="p-3">Colaborador</th>
                  <th className="p-3">Puesto / Área</th>
                  <th className="p-3">Turno</th>
                  <th className="p-3 text-center">Nivel I.L.U.</th>
                  <th className="p-3 text-center">Score</th>
                  <th className="p-3">Fecha Eval.</th>
                  <th className="p-3">Auditor</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#202230] bg-[#121318]">
                {evaluations.filter(ev => {
                  if (!searchFilter) return true;
                  const s = searchFilter.toLowerCase();
                  return ev.employeeName.toLowerCase().includes(s) ||
                    ev.activityName.toLowerCase().includes(s) ||
                    ev.activityCode.toLowerCase().includes(s) ||
                    ev.position.toLowerCase().includes(s) ||
                    ev.area.toLowerCase().includes(s);
                }).map(ev => (
                  <tr key={ev.id} className="hover:bg-white/[0.02]">
                    <td className="p-3 font-mono font-bold text-indigo-400">{ev.activityCode}</td>
                    <td className="p-3 font-medium text-white max-w-[200px] truncate">{ev.activityName}</td>
                    <td className="p-3">
                      <div className="font-bold text-white">{ev.employeeName}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{ev.employeeId}</div>
                    </td>
                    <td className="p-3 text-slate-300">{ev.position} • {ev.area}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[10px]">
                        {ev.shift}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-black font-mono border ${
                        ev.level === 'U' || ev.level === 'O' ? 'bg-emerald-950 text-emerald-300 border-emerald-500/50' :
                        ev.level === 'L' ? 'bg-sky-950 text-sky-300 border-sky-500/50' :
                        'bg-amber-950 text-amber-300 border-amber-500/50'
                      }`}>
                        {ILU_LEVEL_DEFINITIONS[ev.level]?.iconSymbol || '🟢'} NIVEL {ev.level}
                      </span>
                    </td>
                    <td className="p-3 text-center font-mono font-bold text-slate-200">
                      {ev.score || 100}%
                    </td>
                    <td className="p-3 font-mono text-slate-300 text-[11px]">{ev.evaluationDate}</td>
                    <td className="p-3 text-slate-400 text-[11px]">{ev.evaluator}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setEditingEvaluationId(ev.id);
                            setEvaluationForm({ ...ev });
                            setIsEvaluationModalOpen(true);
                          }}
                          className="p-1 text-slate-400 hover:text-sky-400"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteEvaluation(ev.id, e)}
                          className="p-1 text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VISTA 4: CATÁLOGO DE ACTIVIDADES */}
      {/* ========================================================================= */}
      {viewMode === 'activities_list' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-[#161720] p-3.5 rounded-xl border border-[#282a3a]">
            <div>
              <h4 className="font-bold text-sm text-white">Catálogo Maestro de Actividades de Seguridad</h4>
              <p className="text-xs text-slate-400">Administre códigos, nombres, categorías y EPP requerido para las tareas de la planta.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setBulkParsedActivities([]);
                  setBulkErrors([]);
                  setBulkFile(null);
                  setIsBulkActivitiesModalOpen(true);
                }}
                className="px-3 py-1.5 rounded-xl bg-[#1e2029] hover:bg-[#2a2d3b] text-slate-200 border border-[#34374a] text-xs font-bold flex items-center gap-1.5 cursor-pointer"
              >
                <UploadCloud className="w-3.5 h-3.5 text-emerald-400" />
                <span>Carga Masiva</span>
              </button>
              <button
                onClick={() => {
                  setActivityForm({
                    riskLevel: 'alto',
                    category: 'Trabajos Peligrosos',
                    area: activeCostCenter ? activeCostCenter.name : 'Planta General'
                  });
                  setEditingActivityId(null);
                  setIsActivityModalOpen(true);
                }}
                className="px-3.5 py-1.5 rounded-xl bg-[#bc1100] hover:bg-[#d91605] text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-md"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Nueva Actividad</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-[#282a3a]">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#161722] text-slate-400 uppercase text-[10px] tracking-wider border-b border-[#282a3a]">
                <tr>
                  <th className="p-3">Código</th>
                  <th className="p-3">Nombre de la Actividad</th>
                  <th className="p-3">Categoría / Área</th>
                  <th className="p-3">Riesgo</th>
                  <th className="p-3">Norma STPS</th>
                  <th className="p-3">EPP Requerido</th>
                  <th className="p-3 text-center">Evaluados</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#202230] bg-[#121318]">
                {activities.map(act => {
                  const evCount = evaluations.filter(e => e.activityId === act.id || e.activityCode === act.code).length;
                  return (
                    <tr key={act.id} className="hover:bg-white/[0.02]">
                      <td className="p-3 font-mono font-bold text-indigo-400">{act.code}</td>
                      <td className="p-3 font-bold text-white">{act.name}</td>
                      <td className="p-3 text-slate-300">{act.category} • {act.area}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          act.riskLevel === 'critico' ? 'bg-red-500/20 text-red-300 border border-red-500/40' :
                          act.riskLevel === 'alto' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' :
                          'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                        }`}>
                          {act.riskLevel}
                        </span>
                      </td>
                      <td className="p-3 text-slate-400 font-mono">{act.standardCode || 'NOM-STPS'}</td>
                      <td className="p-3 text-slate-300 max-w-[200px] truncate">{act.eppRequired || 'EPP básico'}</td>
                      <td className="p-3 text-center font-mono font-bold text-white">{evCount}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => {
                              setEditingActivityId(act.id);
                              setActivityForm({ ...act });
                              setIsActivityModalOpen(true);
                            }}
                            className="p-1.5 text-slate-400 hover:text-sky-400"
                            title="Editar actividad"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteActivity(act.id, e)}
                            className="p-1.5 text-slate-400 hover:text-red-400"
                            title="Eliminar actividad"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: REGISTRAR / EDITAR ACTIVIDAD INDIVIDUAL */}
      {/* ========================================================================= */}
      {isActivityModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#161720] border border-[#2e3144] rounded-2xl max-w-lg w-full p-6 space-y-4 text-white">
            <div className="flex items-center justify-between border-b border-[#292b3c] pb-3">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-400" />
                {editingActivityId ? 'Editar Actividad de Seguridad' : 'Nueva Actividad de Seguridad'}
              </h3>
              <button
                onClick={() => setIsActivityModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveActivity} className="space-y-3.5 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Código de Actividad *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: LOTO-01, ALT-02"
                    value={activityForm.code || ''}
                    onChange={(e) => setActivityForm({ ...activityForm, code: e.target.value.toUpperCase() })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono uppercase"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Nivel de Riesgo *</label>
                  <select
                    value={activityForm.riskLevel || 'alto'}
                    onChange={(e) => setActivityForm({ ...activityForm, riskLevel: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  >
                    <option value="critico">🔴 Crítico (Peligro de Vida / NOM)</option>
                    <option value="alto">🟠 Alto (Riesgo Significativo)</option>
                    <option value="medio">🟡 Medio (Riesgo Moderado)</option>
                    <option value="bajo">🟢 Bajo (General / Básico)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Nombre Descriptivo de la Actividad *</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Bloqueo y Etiquetado de Energía Peligrosa (LOTO)"
                  value={activityForm.name || ''}
                  onChange={(e) => setActivityForm({ ...activityForm, name: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-bold"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Categoría / Tipo *</label>
                  <input
                    type="text"
                    placeholder="Ej: Trabajos Peligrosos, Químicos"
                    value={activityForm.category || ''}
                    onChange={(e) => setActivityForm({ ...activityForm, category: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Área / Ubicación *</label>
                  <input
                    type="text"
                    placeholder="Ej: Mantenimiento, Planta General"
                    value={activityForm.area || ''}
                    onChange={(e) => setActivityForm({ ...activityForm, area: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Norma o Estándar</label>
                  <input
                    type="text"
                    placeholder="Ej: NOM-004-STPS-1999"
                    value={activityForm.standardCode || ''}
                    onChange={(e) => setActivityForm({ ...activityForm, standardCode: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">EPP Requerido</label>
                  <input
                    type="text"
                    placeholder="Ej: Casco, Lentes, Guantes dieléctricos"
                    value={activityForm.eppRequired || ''}
                    onChange={(e) => setActivityForm({ ...activityForm, eppRequired: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Descripción del Procedimiento</label>
                <textarea
                  rows={2}
                  placeholder="Detalles sobre los pasos clave de seguridad y verificación..."
                  value={activityForm.description || ''}
                  onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-[#292b3c]">
                <button
                  type="button"
                  onClick={() => setIsActivityModalOpen(false)}
                  className="px-4 py-2 text-slate-400 hover:text-white bg-[#101115] rounded-xl font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold cursor-pointer shadow-md"
                >
                  {editingActivityId ? 'Guardar Cambios' : 'Registrar Actividad'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: EVALUACIÓN INDIVIDUAL DE COLABORADOR (I.L.U.) */}
      {/* ========================================================================= */}
      {isEvaluationModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#161720] border border-[#2e3144] rounded-2xl max-w-2xl w-full p-6 space-y-4 text-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-[#292b3c] pb-3">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-emerald-400" />
                  {editingEvaluationId ? 'Actualizar Evaluación de Colaborador' : 'Evaluar Colaborador en Actividad de Seguridad'}
                </h3>
                <p className="text-xs text-slate-400">
                  Asignación del nivel de competencia operativa y destreza en seguridad (Método I.L.U.).
                </p>
              </div>
              <button
                onClick={() => setIsEvaluationModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEvaluation} className="space-y-4 text-xs">
              
              {/* SMART SEARCHABLE ACTIVITY SELECTOR */}
              <div className="relative space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="block text-slate-300 font-bold">
                    Actividad / Tarea de Seguridad a Evaluar *
                  </label>
                  {(() => {
                    const selAct = activities.find(a => a.id === evaluationForm.activityId) || activities.find(a => a.code === evaluationForm.activityCode);
                    if (selAct) {
                      return (
                        <span className="text-[11px] text-slate-400 font-mono">
                          Código: <strong className="text-sky-400">{selAct.code}</strong>
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Search Input Bar with Autocomplete Dropdown */}
                <div className="relative">
                  <div className="relative flex items-center">
                    <Search className="w-4 h-4 absolute left-3 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Escriba para buscar y filtrar actividad por código, nombre o norma..."
                      value={activitySearchQuery}
                      onChange={(e) => {
                        setActivitySearchQuery(e.target.value);
                        setIsActivityDropdownOpen(true);
                      }}
                      onFocus={() => setIsActivityDropdownOpen(true)}
                      className={`w-full bg-[#101115] border ${
                        !evaluationForm.activityId ? 'border-amber-500/50' : 'border-[#2a2c3c]'
                      } rounded-xl pl-9 pr-8 py-2.5 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-sky-500`}
                    />
                    {activitySearchQuery ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActivitySearchQuery('');
                        }}
                        className="absolute right-2.5 p-1 text-slate-400 hover:text-white cursor-pointer"
                        title="Limpiar búsqueda"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <ChevronDown className="w-4 h-4 absolute right-3 text-slate-500 pointer-events-none" />
                    )}
                  </div>

                  {/* Dropdown Suggestions List */}
                  {isActivityDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsActivityDropdownOpen(false)}
                      />
                      <div className="absolute left-0 right-0 top-full mt-1.5 bg-[#161824] border border-[#2e3146] rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto divide-y divide-[#222434]">
                        {filteredModalActivities.length === 0 ? (
                          <div className="p-3 text-center text-xs text-slate-400">
                            No se encontraron actividades que coincidan con "<span className="text-white font-bold">{activitySearchQuery}</span>"
                          </div>
                        ) : (
                          filteredModalActivities.map(act => {
                            const isCurrent = act.id === evaluationForm.activityId || act.code === evaluationForm.activityCode;
                            const riskColor = act.riskLevel === 'critico' ? 'text-red-400 bg-red-950/40 border-red-500/30' :
                                              act.riskLevel === 'alto' ? 'text-amber-400 bg-amber-950/40 border-amber-500/30' :
                                              act.riskLevel === 'medio' ? 'text-sky-400 bg-sky-950/40 border-sky-500/30' :
                                              'text-emerald-400 bg-emerald-950/40 border-emerald-500/30';
                            return (
                              <button
                                type="button"
                                key={act.id}
                                onClick={() => {
                                  setEvaluationForm(prev => ({
                                    ...prev,
                                    activityId: act.id,
                                    activityCode: act.code,
                                    activityName: act.name,
                                    area: prev.area || act.area || ''
                                  }));
                                  setActivitySearchQuery('');
                                  setIsActivityDropdownOpen(false);
                                }}
                                className={`w-full p-2.5 text-left flex items-center justify-between gap-2 hover:bg-white/[0.04] transition-colors cursor-pointer ${
                                  isCurrent ? 'bg-sky-950/40 border-l-2 border-sky-400' : ''
                                }`}
                              >
                                <div className="space-y-0.5 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono font-bold text-xs text-sky-400">[{act.code}]</span>
                                    <span className="text-xs font-semibold text-white truncate">{act.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                    <span>{act.category}</span>
                                    {act.standardCode && <span>• {act.standardCode}</span>}
                                    {act.area && <span>• {act.area}</span>}
                                  </div>
                                </div>

                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${riskColor}`}>
                                    {act.riskLevel}
                                  </span>
                                  {isCurrent && <CheckCircle2 className="w-3.5 h-3.5 text-sky-400" />}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Selected Activity Preview Badge */}
                {(() => {
                  const selAct = activities.find(a => a.id === evaluationForm.activityId) || activities.find(a => a.code === evaluationForm.activityCode);
                  if (selAct && !isActivityDropdownOpen) {
                    return (
                      <div className="p-2.5 rounded-xl bg-[#12131c] border border-[#252838] flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="p-1.5 rounded-lg bg-sky-950/60 border border-sky-500/40 text-sky-400 font-mono font-bold text-xs flex-shrink-0">
                            {selAct.code}
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-white truncate">{selAct.name}</div>
                            <div className="text-[10px] text-slate-400 truncate">
                              {selAct.category} • Riesgo <span className="uppercase text-amber-400 font-bold">{selAct.riskLevel}</span>
                              {selAct.standardCode ? ` • ${selAct.standardCode}` : ''}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setIsActivityDropdownOpen(true);
                          }}
                          className="text-[11px] text-sky-400 hover:text-sky-300 underline font-medium cursor-pointer ml-2 flex-shrink-0"
                        >
                          Cambiar
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}

                {!evaluationForm.activityId && (
                  <p className="text-[10px] text-amber-400">
                    * Seleccione una actividad del buscador desplegable para continuar.
                  </p>
                )}
              </div>

              {/* Collaborator Details */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">No. Nómina / ID Colaborador *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: EMP-1042"
                    value={evaluationForm.employeeId || ''}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, employeeId: e.target.value.toUpperCase() })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono placeholder:text-slate-600"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-400 mb-1">Nombre Completo del Colaborador *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Carlos Morales Hernández"
                    value={evaluationForm.employeeName || ''}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, employeeName: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-bold placeholder:text-slate-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Puesto / Cargo *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Técnico Electromecánico"
                    value={evaluationForm.position || ''}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, position: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white placeholder:text-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Área Operativa *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Mantenimiento, Reactores, Almacén"
                    value={evaluationForm.area || ''}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, area: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white placeholder:text-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Turno de Trabajo *</label>
                  <select
                    value={evaluationForm.shift || 'Turno 1 - Matutino'}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, shift: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  >
                    <option value="Turno 1 - Matutino">Turno 1 - Matutino</option>
                    <option value="Turno 2 - Vespertino">Turno 2 - Vespertino</option>
                    <option value="Turno 3 - Nocturno">Turno 3 - Nocturno</option>
                    <option value="Turno Mixto">Turno Mixto</option>
                    <option value="Administrativo">Administrativo</option>
                  </select>
                </div>
              </div>

              {/* ILU LEVEL SELECTOR (CRITICAL) */}
              <div className="space-y-2 pt-2 border-t border-[#292b3c]">
                <label className="block text-slate-300 font-bold">
                  Nivel de Competencia Evaluado (Método I.L.U.) *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {(['I', 'L', 'U'] as ILULevel[]).map(lvl => {
                    const isSelected = evaluationForm.level === lvl;
                    const def = ILU_LEVEL_DEFINITIONS[lvl];
                    return (
                      <button
                        type="button"
                        key={lvl}
                        onClick={() => {
                          const status = lvl === 'U' ? 'apto' : (lvl === 'L' ? 'en_desarrollo' : 'requiere_refuerzo');
                          setEvaluationForm({ ...evaluationForm, level: lvl, status });
                        }}
                        className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                          isSelected
                            ? `${def.colorBg} ${def.colorBorder} border-2 shadow-lg`
                            : 'bg-[#101115] border-[#2a2c3c] opacity-70 hover:opacity-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`font-black font-mono text-sm ${def.colorText}`}>
                            {def.iconSymbol} {def.badgeText}
                          </span>
                          {isSelected && <CheckCircle2 className={`w-4 h-4 ${def.colorText}`} />}
                        </div>
                        <p className="text-[10px] text-slate-300 mt-1 line-clamp-2">
                          {def.shortDesc}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Calificación Obtenida (0-100%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="Ej: 95"
                    value={evaluationForm.score !== undefined && evaluationForm.score !== null ? evaluationForm.score : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEvaluationForm({ 
                        ...evaluationForm, 
                        score: val === '' ? undefined : Math.min(100, Math.max(0, parseInt(val) || 0)) 
                      });
                    }}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono placeholder:text-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Fecha de Evaluación *</label>
                  <input
                    type="date"
                    required
                    value={evaluationForm.evaluationDate || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, evaluationDate: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Auditor / Evaluador HSEQ</label>
                  <input
                    type="text"
                    placeholder="Ej: Ing. Roberto Silva"
                    value={evaluationForm.evaluator || ''}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, evaluator: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Observaciones / Evidencia de Evaluación</label>
                <textarea
                  rows={2}
                  placeholder="Detalles de desempeño en campo, colocación de EPP, prueba de energía cero, etc."
                  value={evaluationForm.evidenceNotes || ''}
                  onChange={(e) => setEvaluationForm({ ...evaluationForm, evidenceNotes: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white placeholder:text-slate-600"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-[#292b3c]">
                <button
                  type="button"
                  onClick={() => setIsEvaluationModalOpen(false)}
                  className="px-4 py-2 text-slate-400 hover:text-white bg-[#101115] rounded-xl font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!evaluationForm.activityId && !evaluationForm.activityCode}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold cursor-pointer shadow-md disabled:opacity-50"
                >
                  {editingEvaluationId ? 'Guardar Cambios' : 'Registrar Evaluación'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: CARGA MASIVA DE ACTIVIDADES (EXCEL / CSV) */}
      {/* ========================================================================= */}
      {isBulkActivitiesModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#161720] border border-[#2e3144] rounded-2xl max-w-3xl w-full p-6 space-y-4 text-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between border-b border-[#282a3a] pb-3">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  <UploadCloud className="w-5 h-5 text-sky-400" />
                  Carga Masiva de Actividades y Tareas de Seguridad
                </h3>
                <p className="text-xs text-slate-400">
                  Suba un archivo Excel (.xlsx) o CSV con el catálogo de tareas críticas de la planta.
                </p>
              </div>
              <button
                onClick={() => generateActivitiesTemplateExcel()}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Descargar Plantilla Excel</span>
              </button>
            </div>

            {/* Drop Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const f = e.dataTransfer.files?.[0];
                if (f) {
                  setBulkFile(f);
                  parseBulkActivitiesFile(f);
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              className={`p-6 rounded-xl border-2 border-dashed text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-emerald-500 bg-emerald-950/20'
                  : 'border-[#2e3140] bg-[#101115] hover:border-slate-500'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setBulkFile(f);
                    parseBulkActivitiesFile(f);
                  }
                }}
              />
              <FileSpreadsheet className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-xs font-bold text-white">
                {bulkFile ? bulkFile.name : 'Arrastre aquí su archivo Excel (.xlsx) o haga clic para seleccionarlo'}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">Formato oficial con columnas: Código, Nombre, Categoría, Riesgo, Norma, EPP, Descripción</p>
            </div>

            {/* Errors */}
            {bulkErrors.length > 0 && (
              <div className="p-3 rounded-lg bg-red-950/40 border border-red-500/50 text-xs text-red-300 space-y-1">
                <span className="font-bold block">Observaciones detectadas:</span>
                {bulkErrors.slice(0, 3).map((err, i) => (
                  <div key={i} className="text-[11px]">• {err}</div>
                ))}
              </div>
            )}

            {/* Preview of Parsed Items */}
            {bulkParsedActivities.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-emerald-400">
                    ✓ Se detectaron {bulkParsedActivities.length} actividades válidas listas para importar:
                  </span>
                </div>

                <div className="max-h-48 overflow-y-auto rounded-lg border border-[#2e3140] overflow-x-auto">
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-[#101115] text-slate-400 sticky top-0 uppercase text-[9px]">
                      <tr>
                        <th className="p-2">Código</th>
                        <th className="p-2">Nombre Actividad</th>
                        <th className="p-2">Categoría</th>
                        <th className="p-2">Riesgo</th>
                        <th className="p-2">Norma</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#222430]">
                      {bulkParsedActivities.map((a, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.02]">
                          <td className="p-2 font-mono font-bold text-indigo-400">{a.code}</td>
                          <td className="p-2 font-medium text-white">{a.name}</td>
                          <td className="p-2 text-slate-300">{a.category}</td>
                          <td className="p-2 text-amber-400 font-bold uppercase">{a.riskLevel}</td>
                          <td className="p-2 text-slate-400">{a.standardCode}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-[#292b3c]">
              <button
                type="button"
                onClick={() => setIsBulkActivitiesModalOpen(false)}
                className="px-4 py-2 text-slate-400 hover:text-white bg-[#101115] rounded-xl font-bold cursor-pointer text-xs"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={bulkParsedActivities.length === 0}
                onClick={handleConfirmBulkActivities}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold cursor-pointer text-xs shadow-md disabled:opacity-50"
              >
                Importar {bulkParsedActivities.length} Actividades
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 4: CARGA MASIVA DE EVALUACIONES (EXCEL / CSV) */}
      {/* ========================================================================= */}
      {isBulkEvaluationsModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#161720] border border-[#2e3144] rounded-2xl max-w-3xl w-full p-6 space-y-4 text-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between border-b border-[#282a3a] pb-3">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  <UploadCloud className="w-5 h-5 text-emerald-400" />
                  Carga Masiva de Evaluaciones I.L.U.
                </h3>
                <p className="text-xs text-slate-400">
                  Suba un archivo Excel (.xlsx) con las evaluaciones de colaboradores por código de actividad.
                </p>
              </div>
              <button
                onClick={() => generateEvaluationsTemplateExcel(activities)}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Descargar Plantilla Evaluaciones</span>
              </button>
            </div>

            {/* Drop Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const f = e.dataTransfer.files?.[0];
                if (f) {
                  setBulkFile(f);
                  parseBulkEvaluationsFile(f);
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              className={`p-6 rounded-xl border-2 border-dashed text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-emerald-500 bg-emerald-950/20'
                  : 'border-[#2e3140] bg-[#101115] hover:border-slate-500'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setBulkFile(f);
                    parseBulkEvaluationsFile(f);
                  }
                }}
              />
              <FileSpreadsheet className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-xs font-bold text-white">
                {bulkFile ? bulkFile.name : 'Arrastre aquí su archivo de Evaluaciones (.xlsx) o haga clic'}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">Columnas: Código Actividad, No. Nómina, Nombre, Puesto, Área, Turno, Nivel I.L.U., Score, Fecha, Evaluador</p>
            </div>

            {/* Errors */}
            {bulkErrors.length > 0 && (
              <div className="p-3 rounded-lg bg-red-950/40 border border-red-500/50 text-xs text-red-300 space-y-1">
                <span className="font-bold block">Observaciones detectadas:</span>
                {bulkErrors.slice(0, 3).map((err, i) => (
                  <div key={i} className="text-[11px]">• {err}</div>
                ))}
              </div>
            )}

            {/* Preview of Parsed Items */}
            {bulkParsedEvaluations.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-emerald-400">
                    ✓ Se detectaron {bulkParsedEvaluations.length} evaluaciones listas para importar:
                  </span>
                </div>

                <div className="max-h-48 overflow-y-auto rounded-lg border border-[#2e3140] overflow-x-auto">
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-[#101115] text-slate-400 sticky top-0 uppercase text-[9px]">
                      <tr>
                        <th className="p-2">Código</th>
                        <th className="p-2">Colaborador</th>
                        <th className="p-2">Puesto</th>
                        <th className="p-2">Turno</th>
                        <th className="p-2 text-center">Nivel</th>
                        <th className="p-2">Fecha</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#222430]">
                      {bulkParsedEvaluations.map((ev, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.02]">
                          <td className="p-2 font-mono font-bold text-indigo-400">{ev.activityCode}</td>
                          <td className="p-2 font-medium text-white">{ev.employeeName}</td>
                          <td className="p-2 text-slate-300">{ev.position}</td>
                          <td className="p-2 text-slate-400">{ev.shift}</td>
                          <td className="p-2 text-center font-bold font-mono text-emerald-400">Nivel {ev.level}</td>
                          <td className="p-2 text-slate-400">{ev.evaluationDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-[#292b3c]">
              <button
                type="button"
                onClick={() => setIsBulkEvaluationsModalOpen(false)}
                className="px-4 py-2 text-slate-400 hover:text-white bg-[#101115] rounded-xl font-bold cursor-pointer text-xs"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={bulkParsedEvaluations.length === 0}
                onClick={handleConfirmBulkEvaluations}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold cursor-pointer text-xs shadow-md disabled:opacity-50"
              >
                Importar {bulkParsedEvaluations.length} Evaluaciones
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 5: GUÍA METODOLÓGICA I.L.U. */}
      {/* ========================================================================= */}
      {isHelpModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#161720] border border-[#2e3144] rounded-2xl max-w-2xl w-full p-6 space-y-4 text-white">
            <div className="flex items-center justify-between border-b border-[#292b3c] pb-3">
              <h3 className="font-bold text-base flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-amber-400" />
                Definición de Niveles de la Matriz I.L.U. (Seguridad Industrial)
              </h3>
              <button
                onClick={() => setIsHelpModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="bg-amber-950/30 border border-amber-500/40 rounded-xl p-3.5 space-y-1">
                <div className="flex items-center gap-2 font-bold text-amber-300">
                  <span className="text-base">🟠</span>
                  <span>NIVEL I - INDUCCIÓN / EN FORMACIÓN (1er Cuadrante)</span>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  {ILU_LEVEL_DEFINITIONS.I.fullDesc}
                </p>
              </div>

              <div className="bg-sky-950/30 border border-sky-500/40 rounded-xl p-3.5 space-y-1">
                <div className="flex items-center gap-2 font-bold text-sky-300">
                  <span className="text-base">🔵</span>
                  <span>NIVEL L - EN DESARROLLO / SUPERVISADO (2do Cuadrante)</span>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  {ILU_LEVEL_DEFINITIONS.L.fullDesc}
                </p>
              </div>

              <div className="bg-emerald-950/30 border border-emerald-500/40 rounded-xl p-3.5 space-y-1">
                <div className="flex items-center gap-2 font-bold text-emerald-300">
                  <span className="text-base">🟢</span>
                  <span>NIVEL U - AUTÓNOMO / 100% CAPACITADO (3er Cuadrante)</span>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  {ILU_LEVEL_DEFINITIONS.U.fullDesc}
                </p>
              </div>

              <div className="bg-purple-950/30 border border-purple-500/40 rounded-xl p-3.5 space-y-1">
                <div className="flex items-center gap-2 font-bold text-purple-300">
                  <span className="text-base">🟣</span>
                  <span>NIVEL O - INSTRUCTOR / AUDITOR MASTER (4to Cuadrante - Opcional)</span>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  {ILU_LEVEL_DEFINITIONS.O.fullDesc}
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-[#292b3c]">
              <button
                onClick={() => setIsHelpModalOpen(false)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold cursor-pointer"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
